// Sequence diff (LCS) behind the change displays: instead of striking a whole
// text field when only a few words changed, callers mark just the removed tokens
// (old value) and the added tokens (new value). Token-agnostic — see
// helpers/richTextDiff.js for the rich-text word tokens.

// LCS is O(m*n) in time and memory. Above this many cells — counted on the
// differing middle only, after trimming the shared prefix/suffix — the middle is
// reported whole as removed + added to stay snappy.
const MAX_CELLS = 2_000_000; // ≈ 1400 words on each side

const sharedAffixSizes = (ka, kb) => {
  const max = Math.min(ka.length, kb.length);
  let head = 0;
  while (head < max && ka[head] === kb[head]) head += 1;
  let tail = 0;
  while (tail < max - head && ka[ka.length - 1 - tail] === kb[kb.length - 1 - tail]) tail += 1;
  return [head, tail];
};

// LCS walk over key arrays → [{ i, j, op }] (i/j index the token on its side).
const lcsOps = (ka, kb, offset) => {
  const m = ka.length;
  const n = kb.length;
  // LCS length ≤ min(m, n) ≤ sqrt(MAX_CELLS), so 16-bit cells are enough.
  const dp = Array.from({ length: m + 1 }, () => new Uint16Array(n + 1));
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      dp[i][j] = ka[i] === kb[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const ops = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (ka[i] === kb[j]) {
      ops.push({ i: offset + i, j: offset + j, op: 'same' });
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ i: offset + i, op: 'removed' });
      i += 1;
    } else {
      ops.push({ j: offset + j, op: 'added' });
      j += 1;
    }
  }
  for (; i < m; i += 1) ops.push({ i: offset + i, op: 'removed' });
  for (; j < n; j += 1) ops.push({ j: offset + j, op: 'added' });
  return ops;
};

// Generic sequence diff: tokens are compared by keyFn(token). Returns ordered
// [{ op: 'same'|'removed'|'added', oldToken?, newToken? }].
export const diffSequence = (a, b, keyFn) => {
  const ka = a.map(keyFn);
  const kb = b.map(keyFn);
  const [head, tail] = sharedAffixSizes(ka, kb);
  const midA = ka.slice(head, ka.length - tail);
  const midB = kb.slice(head, kb.length - tail);

  const middle = midA.length * midB.length > MAX_CELLS
    ? [
      ...midA.map((_, x) => ({ i: head + x, op: 'removed' })),
      ...midB.map((_, x) => ({ j: head + x, op: 'added' })),
    ]
    : lcsOps(midA, midB, head);

  const same = (i, j) => ({ i, j, op: 'same' });
  const ops = [
    ...Array.from({ length: head }, (_, x) => same(x, x)),
    ...middle,
    ...Array.from({ length: tail }, (_, x) => same(a.length - tail + x, b.length - tail + x)),
  ];

  return ops.map(({ i, j, op }) => ({
    op,
    ...(i !== undefined ? { oldToken: a[i] } : {}),
    ...(j !== undefined ? { newToken: b[j] } : {}),
  }));
};

// Coalesce consecutive entries that share `sameRun(prev, next)` into one part.
export const coalesce = (entries, sameRun, join) => {
  const starts = entries
    .map((entry, i) => (i === 0 || !sameRun(entries[i - 1], entry) ? i : -1))
    .filter((i) => i >= 0);

  return starts.map((start, r) => {
    const run = entries.slice(start, starts[r + 1] ?? entries.length);
    return { ...run[0], text: run.map((e) => e.text).reduce(join) };
  });
};
